// components/FormSettings/FormDesignLayout/FieldSettings.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useFieldEdit } from '../../../contexts/FieldEditContext';
import TopBarFieldSettings from './FieldSettingsUI/TopBarFieldSettings';
import SettingsTabs from '../../../CustomTabs/CustomTabs';
import BasicSettings from './FieldSettingsUI/BasicSettings';
import SingleSelectAdvancedSettings from './FieldSettingsUI/SingleSelectAdvancedSettings';
import MultiSelectAdvancedSettings from './FieldSettingsUI/MultiSelectAdvancedSettings';
import DatePickerSettings from './FieldSettingsUI/DatePickerSettings'; // Import DatePickerSettings
import HeaderSettings from './FieldSettingsUI/HeaderSettings'; // Import HeaderSettings
import ImageFieldSettings from './FieldSettingsUI/ImageFieldSettings'; // Import ImageFieldSettings
import FileBasicSettings from './FieldSettingsUI/FileBasicSettings';

import DependencyManager from '../../FormLists/DependencyManager';
import { useDropdownDependencies } from '../../../contexts/DropdownDependenciesContext'; // Import hook

export const FIELD_SETTINGS_ACCESS_MODE = Object.freeze({
  FULL: 'full',
  DEPENDENCIES_ONLY: 'dependenciesOnly',
});

const FieldSettings = ({
  onSave,
  onNavigateBack,
  initialTab,
  fieldDependencyData,
  onDependenciesUpdated,
  dropdownOptionsOverride = null,
  settingsAccessMode = FIELD_SETTINGS_ACCESS_MODE.FULL,
}) => {
  //  console.log('[FieldSettings] Received props before destructuring:', { initialTab, fieldDependencyData });

  const { selectedFieldForEdit, saveFieldChanges } = useFieldEdit();
  const { dropdownDependencies } = useDropdownDependencies();

  const isDependenciesOnlyMode =
    settingsAccessMode === FIELD_SETTINGS_ACCESS_MODE.DEPENDENCIES_ONLY;

  // Determine if dependencies tab should be displayed based on field type
  const isDependencySupported = selectedFieldForEdit?.metadata?.type === 'single-select';

  // Set the initial tab based on field type and initialTab value
  const defaultTab =
    isDependenciesOnlyMode && isDependencySupported
      ? 'dependencies'
      : (
          initialTab &&
          ((initialTab === 'dependencies' && isDependencySupported) || initialTab === 'advanced')
            ? initialTab
            : 'basic'
        );
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Refs to capture handleDone for trimming/saving options in both single and multi-select
  const multiSelectHandleDoneRef = useRef(null);
  const singleSelectHandleDoneRef = useRef(null);

  // Log initial conditions and tab selection
  //  console.log('[FieldSettings] Initial Tab Determination:', { initialTab, isDependencySupported, activeTab });

  // Update active tab if initialTab changes and is supported
  // NOTE: make this idempotent to avoid unnecessary state churn and re-renders that can steal focus
  useEffect(() => {
    //    console.log('[FieldSettings: settings] selectedFieldForEdit updated:', selectedFieldForEdit);
    let next = 'basic';

    if (isDependenciesOnlyMode && isDependencySupported) {
      next = 'dependencies';
    } else if (initialTab === 'dependencies' && isDependencySupported) {
      next = 'dependencies';
    } else if (initialTab === 'advanced') {
      next = 'advanced';
    }

    setActiveTab((prev) => (prev === next ? prev : next));
    //    console.log('[FieldSettings: settings] Active tab set to:', next);
  }, [
    initialTab,
    isDependencySupported,
    isDependenciesOnlyMode,
    selectedFieldForEdit?.id,
  ]);

  // Handle tab change
  const handleChangeTab = useCallback((event, newValue) => {
    if (isDependenciesOnlyMode) {
      return;
    }

    setActiveTab(newValue);
    //    console.log("[FieldSettings] Tab changed to:", newValue);
  }, [
    isDependenciesOnlyMode,
  ]);

  // Handle Done action - merges field changes and navigates back
  const handleDone = useCallback(async () => {
    if (selectedFieldForEdit) {
      /**
       * Dependency-only mode
       * -----------------------------------------------------------------------
       * Used when FormListsLab opens FieldSettings from a dependency icon.
       *
       * WHY
       *  • Dependency edits are form-specific and are saved immediately through
       *    DropdownDependenciesContext.
       *  • This mode must not expose or flush Basic/Advanced field setting
       *    changes for SmartList-backed fields.
       *  • SmartList option definitions remain centrally governed.
       */
      if (!isDependenciesOnlyMode) {
        // Trim and sync dropdown options for multi-select and single-select
        // (These panels keep local UI state; calling their handleDone flushes into FieldEditContext via updateField)
        if (multiSelectHandleDoneRef.current) {
          try { multiSelectHandleDoneRef.current(); } catch (_) {}
        }
        if (singleSelectHandleDoneRef.current) {
          try { singleSelectHandleDoneRef.current(); } catch (_) {}
        }

        // Enterprise-grade: commit ONLY the buffered deltas (fieldChanges) into FieldMetadataContext + storage,
        // then clear the buffer. This avoids snapshot-saving stale metadata and prevents clobbering.
        try {
          await saveFieldChanges();
        } catch (e) {
          // Non-fatal: you can optionally surface this in UI later.
          console.error('[FieldSettings] saveFieldChanges failed:', e);
        }
      }

      // Dependencies are already saved per change via updateDependenciesForOption
      // in DropdownDependenciesContext, so we don't need to flush them here.

      // Notify that dependencies have been updated (refresh FormListsTable)
      if (onDependenciesUpdated) {
        onDependenciesUpdated();
      }
    }

    // Navigate back after saving (if provided)
    if (onNavigateBack) {
      onNavigateBack();
    }
  }, [
    selectedFieldForEdit,
    saveFieldChanges,
    onNavigateBack,
    onDependenciesUpdated,
    isDependenciesOnlyMode,
  ]);


  // Render Advanced Settings based on field type
  const renderAdvancedSettings = () => {
    const type = selectedFieldForEdit?.metadata?.type;

    if (type === 'single-select') {
      return <SingleSelectAdvancedSettings ref={singleSelectHandleDoneRef} />;
    }
    if (type === 'multi-select') {
      return <MultiSelectAdvancedSettings ref={multiSelectHandleDoneRef} />;
    }
    if (type === 'date-picker') {
      // ⬅️ Date-specific formatting & behavior goes in Advanced
      return <DatePickerSettings />;
    }

    return (
      <Typography sx={{ ml: 2 }}>
        No advanced settings available for this field type.
      </Typography>
    );
  };

  // Render Dependency Settings based on field type
  const renderDependencySettings = () => {
    // Check if dependencies are supported for the field type
    if (!isDependencySupported) {
      return (
        <Typography sx={{ ml: 2 }}>
          No dependency settings available for this field type.
        </Typography>
      );
    }

    // Display a message if no optionId is provided
    if (!fieldDependencyData || !fieldDependencyData.optionId) {
      return (
        <Box sx={{ p: 1.5, maxWidth: '85%', margin: '0 auto' }}>
          <Typography
            variant="body2"
            align="center"
            sx={{
              mt: 2,
              color: 'rgba(0, 0, 0, 0.38)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: '18px', mr: 0.5 }} />
            Please select an option from your dropdown list to set up dependencies.
          </Typography>
        </Box>
      );
    }

    // Render DependencyManager if optionId is provided and dependencies are supported
    //    console.log('[FieldSettings] Rendering DependencyManager with fieldDependencyData:', fieldDependencyData);
    return (
      <DependencyManager
        optionId={fieldDependencyData.optionId}
        optionsSetUFI={fieldDependencyData.optionsSetUFI}
        dependencies={
          Array.isArray(selectedFieldForEdit?.metadata?.dependencies)
            ? selectedFieldForEdit.metadata.dependencies
            : []
        }
        dropdownOptionsOverride={dropdownOptionsOverride}
      />
    );
  };

  // Render the appropriate settings component based on field type (original function retained)
  const renderSettingsComponent = () => {
    const type = selectedFieldForEdit?.metadata?.type;

    if (isDependenciesOnlyMode) {
      return renderDependencySettings();
    }

    if (type === 'image' && activeTab === 'basic') {
      //          console.log('[FieldSettings] Rendering ImageFieldSettings for field ID:', selectedFieldForEdit.id);
      return <ImageFieldSettings fieldId={selectedFieldForEdit.id} />;
    }

    if (activeTab === 'basic') {
      if (type === 'header') {
        return <HeaderSettings fieldId={selectedFieldForEdit.id} />;
      }
      if (type === 'attach') {
        // For file attachment, use the dedicated basic settings panel
        return <FileBasicSettings />;
      }

      // ⬅️ Date-picker uses BasicSettings for the shared metadata
      //     (label, helperText, hint, defaultValue, required, etc.)
      return <BasicSettings />;
    }

    if (activeTab === 'advanced') {
      return renderAdvancedSettings();
    }

    if (activeTab === 'dependencies') {
      return renderDependencySettings();
    }

    return null;
  };

  // 🔒 NEW: Memoize the body so BasicSettings (and other panels) keep instance identity
  //         while you type. This prevents transient unmount/remount cycles that can fire onBlur.
  const settingsBody = useMemo(() => {
    return renderSettingsComponent();
    // NOTE: only re-compute when these *identities* change — not on every metadata patch.
  }, [
    activeTab,
    selectedFieldForEdit?.id,
    selectedFieldForEdit?.metadata?.type,
    isDependencySupported,
    isDependenciesOnlyMode,
    fieldDependencyData?.optionId,
    fieldDependencyData?.optionsSetUFI,
    dropdownOptionsOverride,
  ]);

  // Render only if selectedFieldForEdit exists
  if (!selectedFieldForEdit) {
    return null;
  }

  return (
    <Box
      sx={{
        pt: 0,
        px: 0,
        pb: 2,
        width: '100%',
        maxWidth: '100%',
        height: '100%',
        overflowY: 'auto',
        position: 'relative',
      }}
    >
      <TopBarFieldSettings
        onDone={handleDone}
        onNavigateBack={onNavigateBack}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          backgroundColor: 'inherit',
        }}
      />
      <Typography
        variant="subtitle1"
        sx={{ mt: 1, ml: 2, mb: 2, fontWeight: 'normal', fontSize: '1rem' }}
      >
        <span style={{ fontWeight: 'bold', color: '#003459' }}>Edit: </span>
        {selectedFieldForEdit?.metadata?.label || 'Untitled Field'}
      </Typography>

      {!isDependenciesOnlyMode ? (
        <>
          {/* Settings Tabs - Only show 'dependencies' if supported */}
          <SettingsTabs
            value={activeTab}
            onChange={handleChangeTab}
            showDependenciesTab={isDependencySupported} // Pass the conditional prop here
          />
        </>
      ) : null}

      {/* Render the appropriate settings component (memoized) */}
      {settingsBody}
    </Box>
  );
};

export default React.memo(FieldSettings);
